/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

// Use consistent styling
import 'sanitize.css/sanitize.css';
import { setupIonicReact } from '@ionic/react';

// Import root app
import { App } from 'app';

import { HelmetProvider } from 'react-helmet-async';

import { configureAppStore } from 'store/configureStore';

// Initialize languages
import './locales/i18n';
import { Auth0Provider } from '@auth0/auth0-react';
import {
  ThemeProvider,
  StyledEngineProvider,
  CssBaseline,
} from '@mui/material';
import { lightTheme } from 'utils/theme';
import { ToasterBar } from 'app/components/ToasterBar';
import createCache from '@emotion/cache';
import {
  CacheProvider,
  ThemeProvider as EmotionThemeProvider,
} from '@emotion/react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PaginationManager } from 'app/components/Pagination/slice';
import { resolveAuth0ProviderSettings } from 'utils/auth/auth0ProviderSettings';

setupIonicReact({
  mode: 'md',
});

const store = configureAppStore();
const cache = createCache({
  key: 'provision',
  prepend: true,
});

const auth0Settings = resolveAuth0ProviderSettings();
const MOUNT_NODE = document.getElementById('root') as HTMLElement;
const root = createRoot(MOUNT_NODE);

root.render(
  <Provider store={store}>
    <Auth0Provider
      domain={auth0Settings.domain}
      clientId={auth0Settings.clientId}
      scope={auth0Settings.scope}
      redirectUri={window.location.origin}
      audience={auth0Settings.audience}
      ext-hideHelpLineNumber={true}
      ext-hideLocaleDropdown={true}
      cacheLocation="localstorage"
    >
      <HelmetProvider>
        <CacheProvider value={cache as any}>
          <EmotionThemeProvider theme={lightTheme}>
            {/* TODO: Move ThemeProvider and CssBaseline into a component to control theme at will */}
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={lightTheme}>
                <MuiThemeProvider theme={lightTheme}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <CssBaseline>
                      <PaginationManager>
                        <App />
                      </PaginationManager>
                      <ToasterBar />
                    </CssBaseline>
                  </LocalizationProvider>
                </MuiThemeProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </EmotionThemeProvider>
        </CacheProvider>
      </HelmetProvider>
    </Auth0Provider>
  </Provider>,
);
